import type {To} from 'react-router-dom'
import {ChatMessage, DataObjectId, Hub, User} from 'yougile-model'
import {TaskChatTab} from '@extensions/app-persistence'
import {MessageBoxType} from '../types/message-box'
import {Event} from './event-bus'

export interface OpenChatEventData {
  chatId: DataObjectId
  messageTime?: number
  tab?: TaskChatTab
  autofocus?: boolean
}

export interface CreateRepeatTemplateEventData {
  boardId: DataObjectId
  fromTaskId?: DataObjectId
  templateId?: DataObjectId
}

export const openRepeatTemplatesEvent = (
  data: CreateRepeatTemplateEventData
): Event<CreateRepeatTemplateEventData> => ({
  type: 'createRepeatTemplate',
  data
})

export const openChatEvent = (
  chatId: DataObjectId,
  messageTime?: number,
  tab?: TaskChatTab,
  autofocus?: boolean
): Event<OpenChatEventData> => ({
  type: 'openChat',
  data: {chatId, messageTime, tab, autofocus}
})

export const backFromChatEvent = (): Event<any> => ({
  type: 'backFromChat'
})

export const minimizeChatEvent = (): Event<any> => ({
  type: 'minimizeChat'
})

/**
 * Synchronous or Asynchronous event for checking before navigation.
 * Handlers must return true if navigation should be prevented.
 */
export const beforeNavigateEvent = (to: To): Event<{to?: To}> => ({
  type: 'beforeNavigate',
  data: {to}
})

export const restoreChatEvent = (): Event<any> => ({
  type: 'restoreChat'
})

export const resizeChatEvent = (): Event<any> => ({
  type: 'resizeChat'
})

export const showMessage = (
  message: string,
  status: MessageBoxType
): Event<{message: string; status: MessageBoxType}> => ({
  type: 'showMessage',
  data: {message, status}
})

export const openInvitePopup = (user?: User): Event<{user: User}> => ({
  type: 'openInvitePopup',
  data: user && {user}
})

export const openProjectParticipants = (
  project: Hub,
  newRole?: boolean
): Event<{project: Hub; newRole?: boolean}> => ({
  type: 'openProjectParticipants',
  data: {project, newRole}
})

export const mailboxUpdate = (
  companyId: DataObjectId
): Event<{companyId: DataObjectId}> => ({
  type: 'mailboxUpdate',
  data: {companyId}
})

/**
 * Synchronous event for checking before closing the tab.
 * Handlers must return true if unloading should be prevented.
 */
export const onBeforeBrowserUnloadEvent = (): Event<void> => ({
  type: 'onBeforeBrowserUnload'
})

export const chatReplyStateChangedEvent = (
  messageToReply?: ChatMessage
): Event<{messageToReply?: ChatMessage}> => ({
  type: 'chatReplyStateChanged',
  data: {messageToReply}
})

export const chatEditStateChangedEvent = (
  messageToEdit?: ChatMessage
): Event<{messageToEdit?: ChatMessage}> => ({
  type: 'chatEditStateChanged',
  data: {messageToEdit}
})

export const chatForwardStateChangedEvent = (
  messageToForward?: ChatMessage
): Event<{messageToForward?: ChatMessage}> => ({
  type: 'chatForwardStateChanged',
  data: {messageToForward}
})
